import { useEffect, useRef } from "react";
import { AlertTriangle, AlertCircle, Info, CheckCircle2, X } from "lucide-react";
import { useDialogStore } from "../../stores/dialogStore";

export default function GlobalDialog() {
  const { isOpen, options, close } = useDialogStore();
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Focus confirm button when dialog opens
    setTimeout(() => {
      confirmBtnRef.current?.focus();
    }, 50);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (options.variant) {
      case "danger":
        return {
          icon: <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />,
          btnColor: "bg-red-500 hover:bg-red-600 text-white",
          accentBorder: "border-l-4 border-l-red-500",
        };
      case "warning":
        return {
          icon: <AlertCircle size={20} className="text-amber-500 flex-shrink-0" />,
          btnColor: "bg-accent hover:bg-accent-hover text-white",
          accentBorder: "border-l-4 border-l-amber-500",
        };
      case "success":
        return {
          icon: <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />,
          btnColor: "bg-accent hover:bg-accent-hover text-white",
          accentBorder: "border-l-4 border-l-emerald-500",
        };
      case "info":
      default:
        return {
          icon: <Info size={20} className="text-accent flex-shrink-0" />,
          btnColor: "bg-accent hover:bg-accent-hover text-white",
          accentBorder: "border-l-4 border-l-accent",
        };
    }
  };

  const { icon, btnColor, accentBorder } = getVariantStyles();

  return (
    <div
      className="dialog-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
      onClick={() => close(false)}
    >
      <div
        className={`dialog-panel relative w-full max-w-md bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl overflow-hidden ${accentBorder}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <div className="flex items-center gap-2.5">
            {icon}
            <h3 className="text-sm font-bold text-[var(--text-primary)]">
              {options.title}
            </h3>
          </div>
          <button
            onClick={() => close(false)}
            className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
            {options.message}
          </p>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
          {!options.isAlert && (
            <button
              onClick={() => close(false)}
              className="px-3.5 py-1.5 text-xs font-medium border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              {options.cancelText || "Cancelar"}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            onClick={() => close(true)}
            className={`px-4 py-1.5 text-xs font-medium transition-colors shadow-2xs ${btnColor}`}
          >
            {options.confirmText || "Aceptar"}
          </button>
        </div>
      </div>
    </div>
  );
}
